import { AST, Constant, Expression, Relation, TC, Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { v4 } from 'uuid';
import type { TaxonomyNode } from './type-builder';

/**
 * Recursively converts a logic item (which has a category and an expr)
 * into a TaxonomyNode.
 *
 * @param item A logic item with a category and an expression.
 * @returns The corresponding TaxonomyNode.
 */
function convertLogicItem(
  item: {
    category: string;
    expr: AST.ExprIR;
  },
  rel: Relation
): TaxonomyNode {
  const { category, expr } = item;

  switch (expr.t) {
    case 'attr': {
      const colExpr = rel.ref('from').attr(expr.name).ir();
      if (Ty.isEnum(expr.ty)) {
        Assert.assert(expr.ty.ty.k === 'enum');
        return {
          id: v4(),
          name: category,
          t: 'column-ref',
          columnName: expr.name,
          children: Object.fromEntries(
            expr.ty.ty.t.map((v) => {
              return [
                v,
                {
                  id: v4(),
                  name: v,
                  children: {},
                  t: 'enum-item',
                },
              ];
            })
          ),
        };
      }
      if (colExpr.t === 'categorize') {
        return {
          id: v4(),
          name: category,
          children: Object.fromEntries(
            colExpr.logic.map((c) => {
              return [
                c.category,
                {
                  ...convertLogicItem(c, rel),
                  filter: c.filter ? Expression.fromAst(c.filter) : undefined,
                },
              ];
            })
          ),
          t: 'column-ref',
          columnName: expr.name,
        };
      }
      return {
        id: v4(),
        name: category,
        children: {},
        t: 'column-ref',
        columnName: expr.name,
      };
    }
    case 'categorize': {
      const children: { [key: string]: TaxonomyNode } = {};
      for (const childItem of expr.logic) {
        // Use the category as the key for the child node.
        const child = convertLogicItem(childItem, rel);
        if (child.t === 'inline' || child.t === 'column-ref') {
          child.filter = childItem.filter
            ? Expression.fromAst(childItem.filter)
            : undefined;
        }
        children[childItem.category] = child;
      }

      return {
        id: v4(),
        name: category,
        children,
        t: 'inline',
      };
    }
    case 'scalar': {
      if (Ty.isEnum(expr.ty)) {
        const ty = expr.ty.ty;
        Assert.assert(ty.k === 'enum');
        return {
          id: v4(),
          name: category,
          children: Object.fromEntries(
            ty.t.map((v) => [
              v,
              {
                id: v4(),
                name: v,
                children: {},
                t: 'enum-item',
              },
            ])
          ),
          t: 'inline',
        };
      }
      break;
    }
    case 'case': {
      const areAllProcessable = expr.cases.every(
        (c) =>
          c.then.t === 'attr' ||
          c.then.t === 'categorize' ||
          c.then.t === 'scalar'
      );

      if (areAllProcessable) {
        return {
          id: v4(),
          name: category,
          children: Object.fromEntries(
            expr.cases
              .map((c) => {
                Assert.assert(
                  c.then.t === 'attr' ||
                    c.then.t === 'categorize' ||
                    c.then.t === 'scalar'
                );

                if (c.then.t === 'attr') {
                  //TODO IF c.then.t === 'attr' then we need to expand the column-ref if its an enum etc
                  return [
                    //this is lossy, we lose the column-ref name - maybe the column ref name should be forced to be the column name
                    [
                      c.then.name,
                      {
                        ...convertLogicItem(
                          {
                            category: c.then.name,
                            expr: rel.ref('from').attr(c.then.name).ir(),
                          },
                          rel
                        ),
                        t: 'column-ref',
                      },
                    ],
                  ];
                }
                if (c.then.t === 'categorize') {
                  //TODO this might be wrong
                  return c.then.logic.map((x) => {
                    return [
                      x.category,
                      {
                        ...convertLogicItem(x, rel),
                        filter: x.filter
                          ? Expression.fromAst(x.filter)
                          : undefined,
                      },
                    ];
                  });
                }
                if (c.then.t === 'scalar' && Ty.isEnum(c.then.ty)) {
                  const ty = c.then.ty.ty;
                  Assert.assert(ty.k === 'enum');
                  return ty.t.map((v) => [
                    v,
                    {
                      id: v4(),
                      name: v,
                      children: {},
                      t: 'enum-item',
                    },
                  ]);
                }
                throw new Error('unreachable');
              })
              .flat()
          ),
          t: 'inline',
        };
      }
      break;
    }
  }

  return {
    id: v4(),
    name: category,
    children: {},
    t: 'inline',
  };
}

/**
 * Converts the top-level expression (which is expected to be a 'categorize')
 * into a TaxonomyNode tree. Since the top-level expression does not have a
 * category, we create a dummy root node.
 *
 * @param input The top-level expression.
 * @returns The taxonomy tree.
 */
export function convertExpressionToTaxonomy(
  input: AST.ExprIR,
  rel: Relation
): TaxonomyNode {
  if (input.t === 'attr') {
    const ty = rel.attributes[input.name]?.ty;
    const makeChildren = () => {
      if (!ty) {
        return {};
      }

      if (TC.isEnum(ty)) {
        Assert.assert(ty.k === 'enum');
        return Object.fromEntries(
          ty.t.map((v) => [
            v,
            {
              id: v4(),
              name: v,
              children: {},
              t: 'enum-item' as const,
            },
          ])
        );
      }

      return {};
    };
    return {
      id: v4(),
      name: input.name,
      children: makeChildren(),
      t: 'column-ref',
      columnName: input.name,
    };
  }

  if (input.t !== 'categorize') {
    throw new Error("Top-level expression must have t === 'categorize'");
  }

  const children: { [key: string]: TaxonomyNode } = {};
  for (const item of input.logic) {
    const child = convertLogicItem(item, rel);
    if (child.t === 'inline' || child.t === 'column-ref') {
      child.filter = item.filter ? Expression.fromAst(item.filter) : undefined;
    }
    children[v4()] = child;
  }

  // Create a dummy root node.
  return {
    id: v4(),
    name: 'root',
    children,
    t: 'inline',
  };
}

/**
 * Recursively converts a TaxonomyNode into an AST expression.
 *
 * - For taxonomy nodes with type 'column-ref', it creates an AST with t: "attr".
 * - For taxonomy nodes with type 'inline', it creates an AST with t: "categorize"
 *   and recursively converts its children into the logic array.
 *
 * @param node The taxonomy node to convert.
 * @returns The corresponding AST expression.
 */
export function convertTaxonomyToExpression(
  node: TaxonomyNode,
  rel: Relation
): AST.ExprIR {
  if (node.t === 'column-ref') {
    return rel.ref('from').attr(node.columnName).ir();
  }
  // Convert an 'inline' taxonomy node back to a 'categorize' expression.
  // Each child taxonomy node becomes a logic item.
  const logic = Object.entries(node.children).map(([_, child]) => {
    const filter =
      child.t === 'inline' || child.t === 'column-ref'
        ? child.filter?.ir() ?? null
        : null;

    let expr = convertTaxonomyToExpression(child, rel);

    if (expr.t === 'categorize' && expr.logic.length === 0) {
      expr = Constant(true).ir();
    }

    return {
      category: child.name,
      expr:
        Object.values(child.children).length === 0 && filter !== null
          ? filter
          : expr,
      filter,
    };
  });

  return {
    t: 'categorize',
    logic,
  };
}
